import http from "@/http";
import { ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { Actions, BeruState } from "@/store/beru/types";
import { AxiosError } from "axios";
import { ErrorResponseData } from "@/types";
import { DEFAULT_TAKE } from "@/utils/main";
import { addMonths, format } from "date-fns";

const START_BERU_DATE = '2023-09-20' // Дата первой потребности по проекту Беру
const END_BERU_DATE = format(addMonths(new Date(), 1), 'yyyy-MM-dd')

const actions: ActionTree<BeruState, RootState> & Actions = {
  async getBeruRates({ commit }, pharmId) {
    try {
      if (!pharmId) return false;
      commit("clearBeruRates");
      const { data } = await http.get(`/api/beru-rates/${pharmId}`);
      commit("setBeruRates", data.data);
    } catch (e) {
      throw e as AxiosError<ErrorResponseData>;
    }
  },
  
  async getTakenBeru({ commit, rootState }, { pharmId, skip = 0 }) {
    if (!pharmId) return;

    try {
      const params = new URLSearchParams({
        'filter[startDate]': START_BERU_DATE,
        'filter[endDate]': END_BERU_DATE,
        'filter[pharmId]': String(pharmId),
        skip: String(skip),
        take: String(DEFAULT_TAKE),
      });
      const reqUrl = `${rootState.url}api/admin-exchange/taken?${params}`;
      const { data } = await http.get(reqUrl);
      if (skip === 0) {
        commit("setBeruExchangeTaken", data.data);
      } else {
        commit("appendBeruExchangeTaken", data.data);
      }
      commit("setBeruExchangeTakenMeta", data.meta);
    } catch (e) {
      if (skip === 0) commit("setBeruExchangeTaken", null);
      throw e;
    }
  },

  async getFreeBeru({ commit, rootState }, { pharmId, skip = 0 }) {
    if (!pharmId) return;

    try {
      const params = new URLSearchParams({
        'filter[startDate]': START_BERU_DATE,
        'filter[endDate]': END_BERU_DATE,
        'filter[pharmId]': String(pharmId),
        skip: String(skip),
        take: String(DEFAULT_TAKE),
      });
      const reqUrl = `${rootState.url}api/admin-exchange/free?${params}`;
      const { data } = await http.get(reqUrl);
      if (skip === 0) {
        commit("setBeruExchangeFree", data.data);
      } else {
        commit("appendBeruExchangeFree", data.data);
      }
      commit("setBeruExchangeFreeMeta", data.meta);
    } catch (e) {
      if (skip === 0) commit("setBeruExchangeFree", null);
      throw e;
    }
  },

  async getCloseReasons({ commit, rootState }) {
    try {
      const isBeru = true // Признак, что причины нужны для беру. В модалке создания всегда true

      const { data } = await http.get(`${rootState.urlArmZort}api/close-reason?isBeru=${isBeru}`);
      commit("setCloseReasons", data.data ?? []);

      // Возвращаем id первой причины для последующего запроса getCloseTypeReasons
      return data.data?.[0]?.id;
    } catch (e) {
      throw e as AxiosError<ErrorResponseData>;
    }
  },

  async getCloseTypeReasons({ commit, rootState }, reasonId) {
    try {
      if (!reasonId) return

      const { data } = await http.get(`${rootState.urlArmZort}api/close-type-reason/${reasonId}`);
      commit("setCloseTypeReasons", data.data);
    } catch (e) {
      throw e as AxiosError<ErrorResponseData>;
    }
  },

  async createShiftNeeds({ rootState }, needsObj) {
    try {
        const data = await http.post(`${rootState.urlArmZort}api/shift-needs`, { data: [needsObj] })
        return data.status
    } catch (e) {
        throw e as AxiosError<ErrorResponseData>
    }
  },

  async getBeruDetail({ commit, rootState }, needId) {
    try {
        const { data } = await http.get(`${rootState.url}api/admin-exchange/${needId}`)
        commit("setBeruDetail", data.data);
    } catch (e) {
        throw e as AxiosError<ErrorResponseData>
    }
  },

  async cancelShiftNeeds({ rootState }, needId) {
    try {
        const data = await http.delete(`${rootState.urlArmZort}api/shift-needs/${needId}`)
        return data.status
    } catch (e) {
        throw e as AxiosError<ErrorResponseData>
    }
  },
};

export default actions;
